import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { Article, Feed } from "../types";
import { DateGroup, dateGroupOf } from "../utils/date";
import { surface, text } from "../theme";
import { ArticleCard } from "./ArticleCard";

interface Props {
  articles: Article[];
  feeds: Feed[];
  /** Skips date headings while a search is active, where recency matters less. */
  grouped: boolean;
}

const GROUP_ORDER: DateGroup[] = ["Today", "Yesterday", "This week", "Earlier"];

const gridSx = {
  display: "grid",
  gap: 2,
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, 1fr)",
    lg: "repeat(3, 1fr)",
  },
} as const;

function GroupHeading({ label, count }: { label: string; count: number }) {
  return (
    <Box
      sx={{
        gridColumn: "1 / -1",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        mt: 1,
      }}
    >
      <Typography
        component="h2"
        variant="h2"
        sx={{ color: text.tertiary, textTransform: "uppercase" }}
      >
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color: text.tertiary, opacity: 0.7 }}>
        {count}
      </Typography>
      <Box sx={{ flex: 1, height: 1, backgroundColor: surface.border }} />
    </Box>
  );
}

export function ArticleGrid({ articles, feeds, grouped }: Props) {
  const sourceImages = useMemo(() => {
    const map = new Map<string, string | undefined>();
    for (const feed of feeds) map.set(feed.id, feed.defaultImage);
    return map;
  }, [feeds]);

  const groups = useMemo(() => {
    if (!grouped) return null;
    const now = Date.now();
    const buckets = new Map<DateGroup, Article[]>();
    for (const article of articles) {
      const key = dateGroupOf(article.pubDate, now);
      const bucket = buckets.get(key);
      if (bucket) bucket.push(article);
      else buckets.set(key, [article]);
    }
    return GROUP_ORDER.filter((key) => buckets.has(key)).map((key) => ({
      key,
      items: buckets.get(key) as Article[],
    }));
  }, [articles, grouped]);

  if (!groups) {
    return (
      <Box sx={gridSx}>
        {articles.map((article) => (
          <ArticleCard
            key={article.id}
            article={article}
            sourceImage={sourceImages.get(article.feedId)}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={gridSx}>
      {groups.map((group) => (
        <React.Fragment key={group.key}>
          <GroupHeading label={group.key} count={group.items.length} />
          {group.items.map((article) => (
            <ArticleCard
              key={article.id}
              article={article}
              sourceImage={sourceImages.get(article.feedId)}
            />
          ))}
        </React.Fragment>
      ))}
    </Box>
  );
}
